import { API } from "@/lib/api";
import { getGroupAverageRatings } from "@/utils/ratingSuggestions";
import type { Database } from "@/lib/database.types";

type Player = Database["public"]["Tables"]["players"]["Row"];

/** Create a one-off guest player row (metadata.is_guest) for match registration. */
export async function createGuestPlayerForMatch(
  groupId: string,
  name: string,
  roster: Pick<Player, "skills" | "effort" | "stamina">[]
): Promise<Player> {
  const trimmed = name.trim();
  if (trimmed.length < 2) {
    throw new Error("Guest name must be at least 2 characters");
  }

  const avg = getGroupAverageRatings(roster);
  return API.createPlayer({
    name: trimmed,
    skills: avg.skills,
    effort: avg.effort,
    stamina: avg.stamina,
    group_id: groupId,
    metadata: { is_guest: true },
  });
}

/**
 * Replace ephemeral guest IDs (negative) with persisted player IDs.
 * Reuses roster players when names match; dedupes within one submit.
 */
export async function resolveMatchPlayerIds(
  ids: number[],
  guestPlayerNames: Record<number, string> | undefined,
  groupId: string,
  roster: Player[]
): Promise<number[]> {
  const map = await resolveMatchPlayerIdMap(ids, guestPlayerNames, groupId, roster);
  return ids.map((id) => map.get(id)).filter((id): id is number => id != null);
}

/** Resolve each unique ID once; safe for teams + goalscorers in the same submit. */
export async function resolveMatchPlayerIdMap(
  ids: Iterable<number>,
  guestPlayerNames: Record<number, string> | undefined,
  groupId: string,
  roster: Player[]
): Promise<Map<number, number>> {
  const guestNames = guestPlayerNames ?? {};
  const createdByName = new Map<string, number>();
  const resolved = new Map<number, number>();

  for (const id of new Set(ids)) {
    if (id > 0) {
      resolved.set(id, id);
      continue;
    }

    const name = guestNames[id]?.trim();
    if (!name) continue;

    const key = name.toLowerCase();
    const cached = createdByName.get(key);
    if (cached != null) {
      resolved.set(id, cached);
      continue;
    }

    const existing = roster.find((p) => p.name.toLowerCase() === key);
    if (existing) {
      createdByName.set(key, existing.id);
      resolved.set(id, existing.id);
      continue;
    }

    const created = await createGuestPlayerForMatch(groupId, name, roster);
    createdByName.set(key, created.id);
    roster.push(created);
    resolved.set(id, created.id);
  }

  return resolved;
}
